/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import {FormGroupDirective, UntypedFormBuilder, UntypedFormGroup} from "@angular/forms";
import {ConfigSectionComponent} from "@app/models/components/config-section.component";
import {RegistryContexts} from "@app/models/constants/registry-constants";
import {CommonState} from "@app/store/common/common.state";
import {CD_TYPE_THERAPHY_PROFILE_DIABETES} from "@doctor-panel-modules/patient-treatment-plan/models/cd-type-glycaemia-profile";
import {TpConfigsInfo} from "@doctor-panel-modules/patient-treatment-plan/models/tp-sections-info";
import {PatientTreatmentPlanBaseState} from "@doctor-panel-modules/patient-treatment-plan/store/patient-treatment-plan-base.state";
import {environment} from "@env/environment";
import {hasActionsExecuting} from "@ngxs-labs/actions-executing";
import {Select, Store} from "@ngxs/store";
import {TrecClinicalDataCustom} from "@trec/api/clinical-data";
import {FormEntryBase} from "@trec/components/dynamic-form";
import {StoreAction} from "@trec/components/store";
import {Observable, Subscription} from "rxjs";
import {TpBaseActions} from "../../../store/patient-treatment-plan-base.actions";

/* eslint-disable @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match */

@Component({
  selector: "c3-config-glycaemia-profile",
  templateUrl: "./config-glycaemia-profile.component.html",
  styleUrls: ["./config-glycaemia-profile.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ConfigGlycaemiaProfileComponent
  extends ConfigSectionComponent
  implements OnInit, AfterViewInit, OnDestroy {
  @Select(
    hasActionsExecuting([TpBaseActions.FetchGlycaemiaProfile, TpBaseActions.UpdateGlycaemiaProfile])
  )
  loading$: Observable<boolean>;

  // Constants //////////////////////////////////
  readonly KEY_insulin_sensitivity_factor = "insulin_sensitivity_factor";
  readonly KEY_cho_insulin_ratio = "cho_insulin_ratio";
  readonly KEY_glycemic_target_ranges = "glycemic_target_ranges";
  readonly KEY_general = "general";
  readonly KEY_specific = "specific";

  readonly KEY_before_prandial = "before_prandial";
  readonly KEY_after_prandial = "after_prandial";

  readonly KEY_breakfast = "breakfast";
  readonly KEY_lunch = "lunch";
  readonly KEY_dinner = "dinner";

  readonly KEY_day = "day";
  readonly KEY_night = "night";

  readonly KEY_min = "min";
  readonly KEY_max = "max";

  readonly SECTION_REQUIRED_ERROR = "section_required";

  @Input()
  context: RegistryContexts[];

  // Children components ////////////////////////

  @ViewChild("tpForm", {read: FormGroupDirective, static: true}) formElem: FormGroupDirective;

  // Form ///////////////////////////////////////

  get touched$(): Observable<boolean> {
    return this.form.valueChanges.pipe((v) => v); // TODO impl
  }

  get valid$(): Observable<boolean> {
    return this.extractValidFromForm(this.form);
  }

  formModel: FormEntryBase<any>[];
  form: UntypedFormGroup;

  get formValue(): any {
    return this.form.value;
  }
  get isfFormValue(): any {
    return this.form.value[this.KEY_insulin_sensitivity_factor];
  }
  get cirFormValue(): any {
    return this.form.value[this.KEY_cho_insulin_ratio];
  }
  get gtrFormValue(): any {
    return this.form.value[this.KEY_glycemic_target_ranges];
  }

  private _glycaemiaProfile: TpConfigsInfo;
  @Input()
  public get glycaemiaProfile(): TpConfigsInfo {
    return this._glycaemiaProfile;
  }
  public set glycaemiaProfile(value: TpConfigsInfo) {
    this._glycaemiaProfile = value;
  }

  // Form field selectors
  insulinSensitivityFactorSelector: "general" | "specific" = this.KEY_general;
  insulinChoRatioSelector: "general" | "specific" = this.KEY_general;
  insulinTargetRangeSelector: "general" | "specific" = this.KEY_general;

  // Subscriptions //////////////////////////////
  private subscr = new Subscription();

  constructor(private store: Store, private fb: UntypedFormBuilder) {
    super();

    // Prepare form
    this.form = this.fb.group(
      {
        [this.KEY_insulin_sensitivity_factor]: this.fb.group({
          [this.KEY_general]: [null],
          [this.KEY_specific]: this.fb.group({
            [this.KEY_day]: [null],
            [this.KEY_night]: [null],
          }),
        }),
        [this.KEY_cho_insulin_ratio]: this.fb.group({
          [this.KEY_general]: [null],
          [this.KEY_specific]: this.fb.group({
            [this.KEY_breakfast]: [null],
            [this.KEY_lunch]: [null],
            [this.KEY_dinner]: [null],
          }),
        }),
        [this.KEY_glycemic_target_ranges]: this.fb.group({
          [this.KEY_general]: this.fb.group({
            [this.KEY_min]: [null],
            [this.KEY_max]: [null],
          }),
          [this.KEY_specific]: this.fb.group({
            [this.KEY_before_prandial]: this.fb.group({
              [this.KEY_min]: [null],
              [this.KEY_max]: [null],
            }),
            [this.KEY_after_prandial]: this.fb.group({
              [this.KEY_min]: [null],
              [this.KEY_max]: [null],
            }),
            [this.KEY_night]: this.fb.group({
              [this.KEY_min]: [null],
              [this.KEY_max]: [null],
            }),
          }),
        }),
        // });
      },
      {updateOn: "change"}
    );
  }

  ngOnInit() {
    this.store.dispatch(new TpBaseActions.FetchGlycaemiaProfile());
  }

  ngAfterViewInit() {
    // Start listen for profile data and update form
    this.subscr.add(
      this.store
        .select(PatientTreatmentPlanBaseState.glycaemiaProfile)
        .subscribe((glycaemiaProfile) => {
          if (glycaemiaProfile) {
            const isf = glycaemiaProfile.data[this.KEY_insulin_sensitivity_factor];
            const cir = glycaemiaProfile.data[this.KEY_cho_insulin_ratio];
            const gtr = glycaemiaProfile.data[this.KEY_glycemic_target_ranges];

            if (isf.general) {
              this.insulinSensitivityFactorSelector = this.KEY_general;
            } else {
              this.insulinSensitivityFactorSelector = this.KEY_specific;
            }

            if (cir.general) {
              this.insulinChoRatioSelector = this.KEY_general;
            } else {
              this.insulinChoRatioSelector = this.KEY_specific;
            }

            if (gtr.general) {
              this.insulinTargetRangeSelector = this.KEY_general;
            } else {
              this.insulinTargetRangeSelector = this.KEY_specific;
            }

            this.form.patchValue({
              [this.KEY_insulin_sensitivity_factor]: {
                [this.KEY_general]: isf.general,
                [this.KEY_specific]: isf.specific
                  ? isf.specific
                  : {[this.KEY_day]: null, [this.KEY_night]: null},
              },
              [this.KEY_cho_insulin_ratio]: {
                [this.KEY_general]: cir.general,
                [this.KEY_specific]: cir.specific
                  ? cir.specific
                  : {
                    [this.KEY_breakfast]: null,
                    [this.KEY_lunch]: null,
                    [this.KEY_dinner]: null,
                  },
              },
              [this.KEY_glycemic_target_ranges]: {
                [this.KEY_general]: gtr.general
                  ? gtr.general
                  : {[this.KEY_min]: null, [this.KEY_max]: null},
                [this.KEY_specific]: gtr.specific
                  ? gtr.specific
                  : {
                    [this.KEY_before_prandial]: {[this.KEY_min]: null, [this.KEY_max]: null},
                    [this.KEY_after_prandial]: {[this.KEY_min]: null, [this.KEY_max]: null},
                    [this.KEY_night]: {[this.KEY_min]: null, [this.KEY_max]: null},
                  },
              },
            });
          } else {
            this.form.reset();
          }
        })
    );

    // Add conditional validation
    this.setupRequiredValidation();
  }

  prepareSaveDataAction(): StoreAction | null {
    // Validate
    if (this.form.invalid) {
      return null;
    }

    // Get required data
    const patientId = this.store.selectSnapshot(CommonState.patientId);
    const now = new Date().valueOf();

    if (!patientId) {
      throw new Error(`Try to save glycaemia profile data but patient id is unknown`);
    }

    // Prepare value to send
    const profileData = {};
    profileData[this.KEY_insulin_sensitivity_factor] =
      this.insulinSensitivityFactorSelector === this.KEY_specific
        ? {
          general: null,
          specific: this.areAllFieldsFilled(this.isfFormValue.specific)
            ? this.isfFormValue.specific
            : null,
        }
        : {general: this.isfFormValue.general || null, specific: null};

    profileData[this.KEY_cho_insulin_ratio] =
      this.insulinChoRatioSelector === this.KEY_specific
        ? {
          general: null,
          specific: this.areAllFieldsFilled(this.cirFormValue.specific)
            ? this.cirFormValue.specific
            : null,
        }
        : {general: this.cirFormValue.general || null, specific: null};

    profileData[this.KEY_glycemic_target_ranges] =
      this.insulinTargetRangeSelector === this.KEY_specific
        ? {
          general: null,
          specific: this.areAllFieldsFilled(this.gtrFormValue.specific)
            ? this.gtrFormValue.specific
            : null,
        }
        : {
          general: this.areAllFieldsFilled(this.gtrFormValue.general)
            ? this.gtrFormValue.general
            : null,
          specific: null,
        };

    const savedProfile = this.store.selectSnapshot(PatientTreatmentPlanBaseState.glycaemiaProfile);
    const cd = savedProfile
      ? TrecClinicalDataCustom.create(
        CD_TYPE_THERAPHY_PROFILE_DIABETES,
        {id: savedProfile.id, patientId, version: savedProfile.version},
        {...savedProfile.metadata},
        {...profileData}
      )
      : TrecClinicalDataCustom.create(
        CD_TYPE_THERAPHY_PROFILE_DIABETES,
        {patientId},
        {
          provenance: "medical",
          source: environment.appId,
          start_date: now,
          end_date: now,
          entry_date: now,
        },
        {...profileData}
      );

    return new TpBaseActions.UpdateGlycaemiaProfile({glycaemiaProfile: cd});
  }

  /**
   * Add custom validation to form:
   * - If even one field of one of a section is filled, then all fields
   *   of the section must be filled (fields are required).
   *
   * @param form Form to customize
   */
  private setupRequiredValidation() {
    // Listen for changes
    this.subscr.add(
      this.form.valueChanges.subscribe(() => {
        // Clear "general" or "specific" if alternatively one or other field is filled
        if (this.areAllFieldsFilled(this.isfFormValue.specific)) {
          // -> clear general
        } else if (this.isfFormValue.general) {
          // -> clear specific
        }

        // Toogle errors

        // "insulin_sensitivity_factor" section

        // Check field "filling" -> if only part of fields are filled, mark every field as "required"
        if (
          this.areFieldsEmpty(this.isfFormValue.specific) ||
          this.areAllFieldsFilled(this.isfFormValue.specific)
        ) {
          // ALL fields are empty or ALL fields are filled -> ok, set no errors in form
          this.toggleSectionRequiredError(
            this.form.get(
              `${this.KEY_insulin_sensitivity_factor}.${this.KEY_specific}`
            ) as UntypedFormGroup,
            false
          );
        } else {
          // Form group is only PARTIALLy filled -> not valid
          this.toggleSectionRequiredError(
            this.form.get(
              `${this.KEY_insulin_sensitivity_factor}.${this.KEY_specific}`
            ) as UntypedFormGroup,
            true
          );
        }

        // "cho_insulin_ratio" section

        // Check field "filling" -> if only part of fields are filled, mark every field as "required"
        if (
          this.areFieldsEmpty(this.cirFormValue.specific) ||
          this.areAllFieldsFilled(this.cirFormValue.specific)
        ) {
          // ALL fields are empty or ALL fields are filled -> ok, set no errors in form
          this.toggleSectionRequiredError(
            this.form.get(`${this.KEY_cho_insulin_ratio}.${this.KEY_specific}`) as UntypedFormGroup,
            false
          );
        } else {
          // Form group is only PARTIALLy filled -> not valid
          this.toggleSectionRequiredError(
            this.form.get(`${this.KEY_cho_insulin_ratio}.${this.KEY_specific}`) as UntypedFormGroup,
            true
          );
        }

        // "glycemic_target_ranges" section

        // Check field "filling" -> if only part of fields are filled, mark every field as "required"
        if (
          this.areFieldsEmpty(this.gtrFormValue.general) ||
          this.areAllFieldsFilled(this.gtrFormValue.general)
        ) {
          // ALL fields are empty or ALL fields are filled -> ok, set no errors in form
          this.toggleSectionRequiredError(
            this.form.get(`${this.KEY_glycemic_target_ranges}.${this.KEY_general}`) as UntypedFormGroup,
            false
          );
        } else {
          // Form group is only PARTIALLy filled -> not valid
          this.toggleSectionRequiredError(
            this.form.get(`${this.KEY_glycemic_target_ranges}.${this.KEY_general}`) as UntypedFormGroup,
            true
          );
        }
        // Check field "filling" -> if only part of fields are filled, mark every field as "required"
        if (
          this.areFieldsEmpty(this.gtrFormValue.specific) ||
          this.areAllFieldsFilled(this.gtrFormValue.specific)
        ) {
          // ALL fields are empty or ALL fields are filled -> ok, set no errors in form
          this.toggleSectionRequiredError(
            this.form.get(`${this.KEY_glycemic_target_ranges}.${this.KEY_specific}`) as UntypedFormGroup,
            false
          );
        } else {
          // Form group is only PARTIALLy filled -> not valid
          this.toggleSectionRequiredError(
            this.form.get(`${this.KEY_glycemic_target_ranges}.${this.KEY_specific}`) as UntypedFormGroup,
            true
          );
        }
      })
    );
  }

  /**
   * Set/remove the "section_required" error to all fields (and recusively sub fields) of a form group.
   *
   * @param fg Form group
   * @param hasError Flag to set / remove the error
   */
  private toggleSectionRequiredError(fg: UntypedFormGroup, hasError: boolean) {
    const controls = fg.controls;
    for (const key in controls) {
      if (controls.hasOwnProperty(key)) {
        const c = controls[key];
        if (c instanceof UntypedFormGroup) {
          this.toggleSectionRequiredError(c, hasError);
        } else {
          c.setErrors(hasError ? {[this.SECTION_REQUIRED_ERROR]: hasError} : null);
        }
      }
    }
  }

  /**
   * Recusively checks whether fields of an object are all empty.
   * @param obj Object to check
   */
  private areFieldsEmpty(obj: {[key: string]: any}): boolean {
    let empty = true;

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        if (value instanceof Object) {
          empty = empty && this.areFieldsEmpty(value);
        } else {
          empty = empty && !value && value !== 0;
        }
      }
    }

    return empty;
  }

  /**
   * Recusively checks whether fields of an object are all filled.
   * @param obj Object to check
   */
  private areAllFieldsFilled(obj: {[key: string]: any}): boolean {
    let filled = true;

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];
        if (value instanceof Object) {
          filled = filled && this.areAllFieldsFilled(value);
        } else {
          filled = filled && (value || value === 0);
        }
      }
    }

    return filled;
  }

  resetTouched(): void {
    this.form.markAsUntouched();
  }

  ngOnDestroy() {
    this.subscr.unsubscribe();
  }
}
